import { Link } from "@tanstack/react-router";
import { SiteShell } from "@/components/layout/SiteShell";
import { FeaturedSvg } from "@/components/seo/FeaturedSvg";
import { AdSlot } from "@/components/AdSlot";
import {
  HARI_PENTING,
  getHariPentingForDate,
  getHariPentingForMonth,
} from "@/data/hari-penting";
import { todayISO, parseISO, MONTH_NAMES_ID } from "@/lib/calendar/dates";
import { Sparkles } from "lucide-react";
import { HariPdfButton } from "@/components/HariPdfButton";

const FEATURED_SLUGS = ["ibu", "guru", "kartini", "batik-nasional", "buruh", "pahlawan"];

export function HariHubPage() {
  const today = parseISO(todayISO())!;
  const todayEvents = getHariPentingForDate(todayISO());
  const featured = FEATURED_SLUGS
    .map((s) => HARI_PENTING.find((h) => h.slug === s))
    .filter(Boolean) as typeof HARI_PENTING;
  return (
    <SiteShell>
      <div className="py-6 md:py-10">
        <nav aria-label="Breadcrumb" className="mb-4 text-sm text-muted-foreground">
          <ol className="flex flex-wrap items-center gap-1">
            <li><Link to={"/" as never} className="hover:text-foreground">Beranda</Link></li>
            <li>/</li>
            <li className="text-foreground">Hari Penting</li>
          </ol>
        </nav>

        <header className="rounded-2xl border border-border bg-card p-5 md:p-7">
          <div className="flex flex-col items-start gap-5 md:flex-row md:items-center">
            <FeaturedSvg
              variant="home"
              title="Hari Penting Indonesia"
              subtitle={`${HARI_PENTING.length}+ hari peringatan nasional & internasional`}
              badge="365"
              className="w-full max-w-xs md:w-72 md:flex-shrink-0"
            />
            <div className="flex-1">
              <h1 className="text-2xl font-bold tracking-tight md:text-3xl">
                Daftar Hari Penting Nasional & Internasional
              </h1>
              <p className="mt-2 text-sm text-muted-foreground md:text-base">
                Cek tanggal hari peringatan di Indonesia: dari Hari Kartini, Hari Ibu, Hari Guru, hingga Hari Buruh. Lengkap dengan sejarah, makna, dan tradisinya.
              </p>
              <div className="mt-3 flex flex-wrap gap-2">
                <HariPdfButton buttonLabel="Download PDF Hari Penting" showRekap />
              </div>
            </div>
          </div>
        </header>

        {todayEvents.length > 0 && (
          <section className="mt-6 rounded-xl border border-primary/30 bg-primary/5 p-4">
            <div className="flex items-start gap-3">
              <Sparkles className="mt-1 h-5 w-5 text-primary" />
              <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-primary">
                  Hari Ini
                </p>
                <p className="mt-1 font-semibold">
                  {todayEvents.map((e) => e.name).join(" • ")}
                </p>
                <div className="mt-2 flex flex-wrap gap-2">
                  {todayEvents.map((e) => (
                    <Link
                      key={e.slug}
                      to={`/hari/${e.slug}` as never}
                      className="rounded-full border border-primary/30 bg-background px-3 py-1 text-xs hover:bg-primary hover:text-primary-foreground"
                    >
                      {e.name} →
                    </Link>
                  ))}
                </div>
              </div>
            </div>
          </section>
        )}

        <section className="mt-8">
          <h2 className="text-xl font-semibold">Paling banyak dicari</h2>
          <div className="mt-3 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {featured.map((h) => {
              const [mm, dd] = h.monthDay.split("-").map((n) => parseInt(n, 10));
              return (
                <Link
                  key={h.slug}
                  to={`/hari/${h.slug}` as never}
                  className="group rounded-xl border border-border bg-card p-4 transition hover:border-primary"
                >
                  <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                    {dd} {MONTH_NAMES_ID[mm - 1]}
                  </p>
                  <h3 className="mt-1 text-base font-semibold group-hover:text-primary">
                    {h.name}
                  </h3>
                  <p className="mt-1 text-xs text-muted-foreground">
                    {h.scope === "nasional" ? "Peringatan Nasional" : "Internasional"}
                  </p>
                </Link>
              );
            })}
          </div>
        </section>

        <section className="mt-10">
          <h2 className="text-xl font-semibold">Per Bulan</h2>
          <p className="mt-1 text-sm text-muted-foreground">
            Hari peringatan berdasarkan bulan, klik untuk detail lengkap.
          </p>
          <div className="mt-4 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {Array.from({ length: 12 }, (_, i) => i + 1).map((m) => {
              const list = getHariPentingForMonth(m);
              if (list.length === 0) return null;
              const isCurrent = m === today.m;
              return (
                <div
                  key={m}
                  className={
                    "rounded-xl border bg-card p-4 " +
                    (isCurrent ? "border-primary/40 ring-1 ring-primary/20" : "border-border")
                  }
                >
                  <h3 className="font-semibold">{MONTH_NAMES_ID[m - 1]}</h3>
                  <ul className="mt-2 space-y-1.5 text-sm">
                    {list.map((h) => {
                      const dd = parseInt(h.monthDay.slice(3), 10);
                      return (
                        <li key={h.slug}>
                          <Link
                            to={`/hari/${h.slug}` as never}
                            className="hover:text-primary"
                          >
                            <span className="text-muted-foreground">{dd}</span>{" "}
                            <span className="font-medium">{h.name}</span>
                          </Link>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              );
            })}
          </div>
        </section>

        <section className="mt-10 rounded-xl border border-border bg-card p-5 md:p-6">
          <h2 className="text-xl font-semibold">Tanya Jawab</h2>
          <div className="mt-4 space-y-4 text-sm">
            <div>
              <p className="font-semibold">Apa itu hari peringatan nasional?</p>
              <p className="mt-1 text-muted-foreground">
                Hari peringatan nasional adalah tanggal khusus yang ditetapkan pemerintah Indonesia (umumnya melalui Keputusan Presiden) untuk mengenang peristiwa, tokoh, atau nilai penting bagi bangsa. Tidak semua hari peringatan adalah hari libur.
              </p>
            </div>
            <div>
              <p className="font-semibold">Apakah hari peringatan = hari libur?</p>
              <p className="mt-1 text-muted-foreground">
                Tidak selalu. Hanya hari peringatan tertentu yang ditetapkan sebagai libur nasional, seperti Hari Kemerdekaan (17 Agustus), Hari Buruh (1 Mei), dan Hari Lahir Pancasila (1 Juni).
              </p>
            </div>
            <div>
              <p className="font-semibold">Bagaimana cara mengetahui hari peringatan minggu ini?</p>
              <p className="mt-1 text-muted-foreground">
                Buka halaman bulan berjalan atau{" "}
                <Link to={"/hari-ini" as never} className="text-primary hover:underline">
                  Hari Ini
                </Link>{" "}
                untuk melihat peringatan harian.
              </p>
            </div>
          </div>
        </section>

        <div className="mt-8">
          <AdSlot size="rectangle" position="hari-hub" />
        </div>
      </div>
    </SiteShell>
  );
}